import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import InputMask from "react-input-mask";
import { RootState, actions } from "../../state/reducer";
import { zodResolver } from "@hookform/resolvers/zod";
import { editProfileSchema, EditProfileSchemaType } from "../../schemas";
import { backend } from "../../services/backend";
import { toast } from "sonner";
import BottomSaveContainer from "../savectnr";
import { userBackend } from "../../services";

const EditUserProfileForm = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm<EditProfileSchemaType>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {},
  });

  const State = useSelector((state: RootState) => state);
  const [dataToSave, setDataToSave] = useState<EditProfileSchemaType | null>(
    null
  );
  const [phoneOccupied, setPhoneOccupied] = useState<string | null>(null);
  const [initialPhone, setInitialPhone] = useState<string | null>(null); // Начальное значение телефона
  const dispatch = useDispatch();

  const session = State.session;

  if (!session) {
    return null;
  }

  useEffect(() => {
    userBackend
      .getUserProfile(session.id)
      .then((res) => {
        console.log("Res: ", res);
        if (res) {
          // Устанавливаем значения в форму
          dispatch(actions.currentUserProfile(res.data));
          const initialData = {
            user_id: res?.data.user_id || session.id,
            phone: res?.data.phone || "",
            address: res?.data.address || "",
            whatsapp: res?.data.whatsapp || "",
            telegram: res?.data.telegram || "",
            first_name: res?.data.first_name || "",
            last_name: res?.data.last_name || "",
            birth_date: res?.data.birth_date || "",
            city: res?.data.city || "",
            date_employed: res?.data.employ_date || "",
            date_unemployed: res?.data.unemploy_date || "",
          };
          reset(initialData);
          setInitialPhone(initialData.phone); // Сохраняем начальное значение телефона
        } else {
          // Если данных нет, сбрасываем значения
          reset({});
          setInitialPhone(null);
        }
      })
      .catch((e) => {
        console.error("Ошибка загрузки данных пользователя:", e.message);
        reset({}); // Заполняем пустые значения
        setInitialPhone(null);
      });
  }, [session.id]);

  const onSubmit = (data: EditProfileSchemaType) => {
    console.log("Данные формы:", data);
  };

  const formValues = watch();
  useEffect(() => {
    const subscription = watch(
      (value) =>
        value &&
        setDataToSave({
          ...value,
          phone: value.phone || "",
          user_id: session.id,
        })
    );
    return () => subscription.unsubscribe();
  }, [watch]);

  const checkPhoneAvailability = async (phone: string) => {
    try {
      backend
        .checkDataHold({
          phone,
        })
        .then((result) => {
          if (result.hold) {
            toast(`Пользователь с таким номером телефона уже существует`);
          }
        })
        .catch(() => {
          toast("Не удалось проверить, есть ли уже такой пользователь");
        });
    } catch (e) {
      console.error("Ошибка проверки номера телефона:", e.message);
      toast("Не удалось проверить, занят ли номер телефона");
    }
  };

  useEffect(() => {
    const phone = formValues.phone;
    if (phone && phone.length === 16 && phone !== initialPhone) {
      // Проверяем, что номер введен полностью и отличается от начального
      checkPhoneAvailability(phone);
    } else {
      setPhoneOccupied(null); // Сбрасываем предупреждение, если номер не изменился
    }
  }, [formValues.phone, initialPhone]);

  const saveAction = () => {
    if (!dataToSave) return;
    userBackend
      .updateUserProfile({ ...dataToSave, user_id: session.id })
      .then((res) => {
        if (res) {
          toast("Данные профиля успешно обновлены!");
        } else {
          toast("Не удалось обновить данные профиля.");
        }
      })
      .catch((e) => {
        toast("Ошибка сохранения данных профиля:", e.message);
      });
  };

  return (
    <div className="form">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="fullPageForm columnCenter"
      >
        <div className="panelInner">
          <div className="flex between inputRow">
            <div className="column50">
              <label>
                Имя
                <span className="requireSymbol" />
              </label>
              <div className="inputFullWidth">
                <input {...register("first_name")} />
                {errors.first_name && <p>{errors.first_name.message}</p>}
              </div>
            </div>

            <div className="column50">
              <label>
                Фамилия
                <span className="requireSymbol" />
              </label>
              <div className="inputFullWidth">
                <input {...register("last_name")} />
                {errors.last_name && <p>{errors.last_name.message}</p>}
              </div>
            </div>
          </div>
          <div className="flex between inputRow">
            <div className="column50">
              <label>
                Номер телефона (для связи)
                <span className="requireSymbol" />
              </label>
              <div className="inputFullWidth">
                <Controller
                  control={control}
                  name="phone"
                  defaultValue=""
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      mask="+9 (999) 999-99-99"
                      placeholder="+7 (___) ___-__-__"
                      onBlur={field.onBlur} // Передаем событие blur вручную
                      inputRef={field.ref} // Указываем реф для InputMask
                    >
                      {(inputProps: any) => <input {...inputProps} />}
                    </InputMask>
                  )}
                />
                {phoneOccupied && (
                  <p style={{ color: "red" }}>{phoneOccupied}</p>
                )}
              </div>
            </div>

            <div className="column50">
              <label>
                Номер телефона (WhatsApp)
                <span className="requireSymbol" />
              </label>
              <div className="inputFullWidth">
                <input {...register("whatsapp")} />
                {errors.whatsapp && <p>{errors.whatsapp.message}</p>}
              </div>
            </div>
          </div>
          <div className="flex between inputRow">
            <div className="column50">
              <label>
                Telegram
                <span className="requireSymbol" />
              </label>
              <div className="inputFullWidth">
                <input {...register("telegram")} />
                {errors.telegram && <p>{errors.telegram.message}</p>}
              </div>
            </div>

            <div className="column50">
              <label>
                Город
                <span className="requireSymbol" />
              </label>
              <div className="inputFullWidth">
                <input {...register("city")} />
                {errors.city && <p>{errors.city.message}</p>}
              </div>
            </div>
          </div>
          <div className="inputRow">
            <label>
              {`Дата рождения:`}
              <span className="requireSymbol" />
            </label>
            <div className="inputFullWidth">
              <input {...register("birth_date")} type="date" />
            </div>
            {errors.birth_date && <p>{errors.birth_date.message}</p>}
          </div>

          <div className="inputRow">
            <label>{`Комментарий:`}</label>
            <div className="inputFullWidth">
              <input {...register("address")} />
            </div>
            {errors.address && <p>{errors.address.message}</p>}
          </div>
        </div>
      </form>
      {dataToSave && <BottomSaveContainer onSave={saveAction} />}
    </div>
  );
};

export default EditUserProfileForm;
